import { VerityAssessmentType } from "@verity/database/prisma";
import styles from "./ReviewStep.module.css";
import {
	AssessmentTypeBlock,
	ConfirmationSummaryList,
	EditQuizStep,
	EditQuizWrapperRef,
	QuizQuestionBlock,
	RenderableProctoringKey,
	VerityAssessmentDetailsBlock,
	VerityAssessmentPageName,
	VerityAssignmentInstructionsBlock,
} from "@verity/components";
import { useContext } from "react";
import { PortalControllerContext } from "../../../context";
import { UseFetchProctoringDataReturn } from "../../settings/useFetchProctoringData";
import { CoreCard } from "@verity/components/core";
import QuizIcon from "../../../../images/portal/verity-quiz.svg?react";
import AssignmentIcon from "../../../../images/portal/verity-assignment.svg?react";
import {
	getCourse,
	getFileTooBigErrorMessage,
	getModule,
	isNumAttemptsSet,
	QuizDraftQuestion,
} from "./verityAssessmentHelper";
import InstructionsPreview from "./InstructionsPreview";
import { Module } from "@verity/api-client/types";
import { AddInstructionsReturn } from "./hooks/useAddInstructions";
import { AssessmentDetailsReturn } from "./hooks/useAssessmentDetails";
import { Course } from "./hooks/useFetchVerityAssessmentInfo";
import { capitalizeFirstLetter } from "../../../../reports/shared/helpers";

type ReviewStepProps = {
	assessmentDetails: AssessmentDetailsReturn;
	assessmentType: VerityAssessmentType;
	instructions: Pick<AddInstructionsReturn, "instructions" | "uploadedFiles">;
	editQuizData?: ReturnType<EditQuizWrapperRef["getQuizData"]>;
	onEdit: (
		step: VerityAssessmentPageName,
		editQuizStep?: EditQuizStep,
		proctoringOptionToFocus?: RenderableProctoringKey,
	) => void;
	enforcedProfilesOnly: boolean;
	courses: Course[];
	modules: Module[];
	questions: QuizDraftQuestion[];
	hideAssessmentTypeEdit?: boolean;
} & UseFetchProctoringDataReturn;

export default function ReviewStep({
	assessmentDetails,
	assessmentType,
	instructions,
	editQuizData,
	onEdit,
	applications,
	extensions,
	enforcedProfilesOnly,
	courses,
	modules,
	questions,
	hideAssessmentTypeEdit = false,
}: ReviewStepProps) {
	const { instituteSettings, verityInstituteSettings, portalContext } = useContext(PortalControllerContext);
	const { lms } = portalContext;

	const { assessmentDuration, endDate, lateSubmissionDate, startDate, allowLateSubmissions } = assessmentDetails.timing;
	const { isSynced, selectedCourseId, selectedModuleId, assignmentMaxScore } = assessmentDetails.course;
	const { allowedFileTypes, numberOfAttempts, scoringStrategy } = assessmentDetails.submission;
	const { assessmentDescription, assessmentTitle } = assessmentDetails.generalInfo;

	const { instructions: instructionText, uploadedFiles } = instructions;

	const uploadedFilesWithErrorMessages = uploadedFiles.map((file) => ({
		file,
		errorMessage: getFileTooBigErrorMessage(file),
	}));

	const totalQuestions = questions.length;
	const totalPoints = questions.reduce((sum, q) => sum + q.correctionPoints, 0);

	return (
		<CoreCard slotProps={{ bodyWrapper: { className: styles.reviewStepCard } }}>
			<AssessmentTypeBlock
				type={assessmentType}
				icon={assessmentType === "assignment" ? <AssignmentIcon /> : <QuizIcon />}
				onEdit={hideAssessmentTypeEdit ? undefined : () => onEdit("assessmentType")}
			/>
			<VerityAssessmentDetailsBlock
				duration={assessmentDuration}
				endDate={endDate}
				lateSubmissionDate={allowLateSubmissions ? lateSubmissionDate : null}
				startDate={startDate}
				allowedFileTypes={
					assessmentType === "assignment" ? allowedFileTypes.map((fileType) => fileType.label) : undefined
				}
				numAttemptsAllowed={numberOfAttempts}
				description={assessmentDescription}
				title={assessmentTitle}
				syncWithGradebook={lms ? isSynced : undefined}
				courseName={getCourse(courses, selectedCourseId)?.name}
				moduleName={getModule(modules, selectedModuleId)?.name}
				assignmentMaxScore={assessmentType === "assignment" ? assignmentMaxScore : undefined}
				scoringStrategy={isNumAttemptsSet(numberOfAttempts) ? capitalizeFirstLetter(scoringStrategy) : undefined}
				onEdit={() => onEdit("assessmentDetails")}
			/>
			{assessmentType === "assignment" && (
				<VerityAssignmentInstructionsBlock files={uploadedFilesWithErrorMessages} onEdit={() => onEdit("instructions")}>
					<InstructionsPreview instruction={instructionText} />
				</VerityAssignmentInstructionsBlock>
			)}
			{assessmentType === "quiz" && (
				<QuizQuestionBlock
					totalQuestions={totalQuestions}
					totalPoints={totalPoints}
					icon={<QuizIcon />}
					onEdit={() => onEdit("questions")}
				/>
			)}
			<ConfirmationSummaryList
				numExempted={editQuizData?.numExempted}
				handleGoToStep={(editQuizStep, proctoringOptionToFocus) =>
					onEdit("editQuiz", editQuizStep, proctoringOptionToFocus)
				}
				applications={applications}
				extensions={extensions}
				optionRelatedInstituteSettings={instituteSettings}
				enforcedProfilesOnly={enforcedProfilesOnly}
				proctoringOption={editQuizData?.proctoringOption}
				proctoringAssessmentConfiguration={editQuizData?.proctoringAssessmentConfiguration}
				pendingLiveSessions={editQuizData?.pendingLiveSessions}
				quizType={assessmentType === "assignment" ? "verityAssignment" : "verityQuiz"}
				verityInstituteSettings={verityInstituteSettings}
				activeProfile={editQuizData?.activeProfile}
			/>
		</CoreCard>
	);
}
